import { ResumeAnalysis, RewriteResponse } from '../types';
import { getCurrentUser } from './auth';

const API_BASE_URL = "https://resumedoctorbackend-81529650669.us-east1.run.app"
// const API_BASE_URL = "http://localhost:8000"

const getAuthHeader = async (): Promise<Headers> => {
  const user = await getCurrentUser();
  if (!user) {
    throw new Error('User not authenticated');
  }
  const token = await user.getIdToken();
  console.log('Auth token:', token ? 'Token exists' : 'No token'); // Debug log
  const headers = new Headers();
  headers.append('Authorization', `Bearer ${token}`);
  headers.append('Content-Type', 'application/json');
  return headers;
};

export const analyzeResume = async (file: File, jobDescription: string, jobRole: string): Promise<ResumeAnalysis> => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('job_description', jobDescription);
  formData.append('job_role', jobRole);

  const headers = await getAuthHeader();
  headers.delete('Content-Type'); // Let the browser set the correct content type for FormData

  const response = await fetch(`${API_BASE_URL}/analyze`, {
    method: 'POST',
    headers,
    body: formData,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ detail: 'Server error' }));
    throw new Error(errorData.detail || `Server error (${response.status})`);
  }

  const data = await response.json();
  return {
    ...data.analysis,
    resume_text: data.resume_text
  };
};

export interface ATSResume {
  id: string;
  company_name: string;
  job_title: string;
  resume_url: string;
  ats_score?: number;
  analysis?: any;
  created_at: string;
}

export const getATSResumes = async (search?: string, dateFilter?: string): Promise<ATSResume[]> => {
  try {
    const headers = await getAuthHeader();
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    if (dateFilter) params.append('date_filter', dateFilter);
    
    const response = await fetch(`${API_BASE_URL}/ats-resumes?${params.toString()}`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to fetch ATS resumes');
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching ATS resumes:', error);
    throw error;
  }
};

export const getATSResume = async (resumeId: string): Promise<ATSResume> => {
  try {
    const headers = await getAuthHeader();
    const response = await fetch(`${API_BASE_URL}/ats-resumes/${resumeId}`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Server error' }));
      throw new Error(errorData.detail || `Server error (${response.status})`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching ATS resume:', error);
    throw error;
  }
};

export const rewriteResume = async (
  resumeText: string,
  jobDescription: string,
  suggestions: string[],
  improvements: string[],
  companyName: string,
  jobTitle: string,
  jobRole: string
): Promise<RewriteResponse> => {
  const headers = await getAuthHeader();
  const response = await fetch(`${API_BASE_URL}/rewrite`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      resume_text: resumeText,
      job_description: jobDescription,
      suggestions,
      improvements,
      company_name: companyName,
      job_title: jobTitle,
      job_role: jobRole
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ detail: 'Server error' }));
    throw new Error(errorData.detail || `Server error (${response.status})`);
  }

  return response.json();
};

export const generateExperiences = async (jobRole: string, jobUrl?: string): Promise<string[]> => {
  try {
    const headers = await getAuthHeader();
    const response = await fetch(`${API_BASE_URL}/generate-experiences-from-link`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ 
        job_role: jobRole,
        job_url: jobUrl || null
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Server error' }));
      if (response.status === 400) {
        throw new Error(`Invalid request: ${errorData.detail}`);
      }
      throw new Error(errorData.detail || `Server error (${response.status})`);
    }

    const data = await response.json();
    return data.experiences;
  } catch (error) {
    console.error('Error generating experiences:', error);
    if (error instanceof TypeError && error.message === 'Failed to fetch') {
      throw new Error('Failed to fetch: Unable to connect to the server');
    }
    throw error;
  }
};

interface CoverLetterRequest {
  companyName: string;
  jobTitle: string;
  jobDescription: string;
  yourName: string;
  tone: string;
}

export const generateCoverLetter = async (request: CoverLetterRequest): Promise<string> => {
  try {
    const headers = await getAuthHeader();
    const response = await fetch(`${API_BASE_URL}/generate-cover-letter`, {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Server error' }));
      if (response.status === 400) {
        throw new Error(`Invalid request: ${errorData.detail}`);
      }
      throw new Error(errorData.detail || `Server error (${response.status})`);
    }

    const data = await response.json();
    return data.coverLetter;
  } catch (error) {
    console.error('Error generating cover letter:', error);
    if (error instanceof TypeError && error.message === 'Failed to fetch') {
      throw new Error('Failed to fetch: Unable to connect to the server');
    }
    throw error;
  }
};

interface UserSettings {
  name: string;
  email: string;
  careerStage: 'new_grad' | 'junior' | 'senior' | 'staff';
  interestedRoles: string[];
  resumeText: string;
  resumeUrl?: string;
  resumeFilename?: string;
}

export const getUserSettings = async (): Promise<UserSettings> => {
  try {
    const headers = await getAuthHeader();
    console.log('Request headers:', Object.fromEntries(headers.entries())); // Debug log
    
    // Add timeout to the fetch request
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
    
    const response = await fetch(`${API_BASE_URL}/user-settings`, {
      method: 'GET',
      headers,
      signal: controller.signal
    });
    
    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Server error' }));
      console.error('Error response:', errorData); // Debug log
      throw new Error(errorData.detail || `Server error (${response.status})`);
    }

    const data = await response.json();
    return data.settings;
  } catch (error: unknown) {
    console.error('Error fetching user settings:', error);
    if (error instanceof TypeError && error.message === 'Failed to fetch') {
      throw new Error('Unable to connect to the server. Please make sure the backend server is running.');
    }
    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error('Request timed out. Please try again.');
    }
    throw error;
  }
};

export const updateUserSettings = async (settings: UserSettings): Promise<void> => {
  try {
    const headers = await getAuthHeader();
    const response = await fetch(`${API_BASE_URL}/user-settings`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({ settings }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Server error' }));
      throw new Error(errorData.detail || `Server error (${response.status})`);
    }
  } catch (error) {
    console.error('Error updating user settings:', error);
    throw error;
  }
};

export const uploadResume = async (file: File): Promise<{ url: string; filename: string }> => {
  try {
    const headers = await getAuthHeader();
    headers.delete('Content-Type'); // Let the browser set the correct content type for FormData
    
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${API_BASE_URL}/upload-resume`, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Server error' }));
      throw new Error(errorData.detail || `Server error (${response.status})`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error uploading resume:', error);
    throw error;
  }
};

interface JobResult {
  title: string;
  link: string;
  company: string;
  location: string;
  postedDate: string;
}

export const searchJobs = async (jobRoles: string[], keywords: string[]): Promise<JobResult[]> => {
  try {
    const headers = await getAuthHeader();
    const response = await fetch(`${API_BASE_URL}/search-jobs`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        job_roles: jobRoles,
        keywords: keywords
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Server error' }));
      throw new Error(errorData.detail || `Server error (${response.status})`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error searching jobs:', error);
    throw error;
  }
};

interface FeedbackRequest {
  email: string;
  message: string;
  created_at?: string;
}

export const submitFeedback = async (feedback: FeedbackRequest): Promise<void> => {
  try {
    const headers = await getAuthHeader();
    const response = await fetch(`${API_BASE_URL}/feedback`, {
      method: 'POST',
      headers,
      body: JSON.stringify(feedback),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Server error' }));
      throw new Error(errorData.detail || `Server error (${response.status})`);
    }
  } catch (error) {
    console.error('Error submitting feedback:', error);
    throw error;
  }
};

// Job Management Types
export enum JobStatus {
  APPLIED = "applied",
  INTERVIEWING = "interviewing",
  OFFERED = "offered",
  REJECTED = "rejected"
}

export interface Job {
  id: string;
  user_id: string;
  title: string;
  company: string;
  status: JobStatus;
  date: string;
  location?: string;
  salary?: string;
  description?: string;
  url?: string;
  created_at: string;
  updated_at: string;
}

export interface JobCreate {
  title: string;
  company: string;
  status: JobStatus;
  date: string;
  location?: string;
  salary?: string;
  description?: string;
  url?: string;
}

export interface JobUpdate {
  title?: string;
  company?: string;
  status?: JobStatus;
  date?: string;
  location?: string;
  salary?: string;
  description?: string;
  url?: string;
}

// Job Management API Functions
export const createJob = async (job: JobCreate): Promise<Job> => {
  try {
    const headers = await getAuthHeader();
    const response = await fetch(`${API_BASE_URL}/jobs`, {
      method: 'POST',
      headers,
      body: JSON.stringify(job),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Server error' }));
      throw new Error(errorData.detail || `Server error (${response.status})`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error creating job:', error);
    throw error;
  }
};

export const getJobs = async (): Promise<Job[]> => {
  try {
    const headers = await getAuthHeader();
    const response = await fetch(`${API_BASE_URL}/jobs`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Server error' }));
      throw new Error(errorData.detail || `Server error (${response.status})`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching jobs:', error);
    throw error;
  }
};

export const updateJob = async (jobId: string, jobUpdate: JobUpdate): Promise<Job> => {
  try {
    const headers = await getAuthHeader();
    const response = await fetch(`${API_BASE_URL}/jobs/${jobId}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(jobUpdate),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Server error' }));
      if (response.status === 404) {
        throw new Error('Job not found');
      }
      if (response.status === 403) {
        throw new Error('Not authorized to update this job');
      }
      throw new Error(errorData.detail || `Server error (${response.status})`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating job:', error);
    throw error;
  }
};

export const deleteJob = async (jobId: string): Promise<void> => {
  try {
    const headers = await getAuthHeader();
    const response = await fetch(`${API_BASE_URL}/jobs/${jobId}`, {
      method: 'DELETE',
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Server error' }));
      if (response.status === 404) {
        throw new Error('Job not found');
      }
      if (response.status === 403) {
        throw new Error('Not authorized to delete this job');
      }
      throw new Error(errorData.detail || `Server error (${response.status})`);
    }
  } catch (error) {
    console.error('Error deleting job:', error);
    throw error;
  }
};

export const updateJobStatus = async (jobId: string, status: JobStatus): Promise<Job> => {
  try {
    const headers = await getAuthHeader();
    const params = new URLSearchParams({ status });
    const response = await fetch(`${API_BASE_URL}/jobs/${jobId}/status?${params.toString()}`, {
      method: 'PATCH',
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Server error' }));
      if (response.status === 404) {
        throw new Error('Job not found');
      }
      if (response.status === 403) {
        throw new Error('Not authorized to update this job');
      }
      throw new Error(errorData.detail || `Server error (${response.status})`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating job status:', error);
    throw error;
  }
}; 